import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { PlatformMap } from "@/components/PlatformMap";
import { FinlandCitiesMap } from "@/components/FinlandCitiesMap";
import { useLanguage } from "@/i18n/LanguageContext";

export const Route = createFileRoute("/map")({
  head: () => ({
    meta: [
      { title: "Explore Finland on the Map | What" },
      {
        name: "description",
        content:
          "Discover every place, sauna, restaurant, activity and event on the What platform on one interactive map of Finland. Search by area, filter by category or travel region, and find what's near you.",
      },
      { property: "og:title", content: "Explore Finland on the Map | What" },
      {
        property: "og:description",
        content:
          "One interactive map for every experience on the What platform — filter by category, region, and location.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: MapPage,
  errorComponent: ({ error }) => (
    <div className="p-6 text-navy" role="alert">
      Failed to load the map: {(error as Error).message}
    </div>
  ),
  notFoundComponent: () => <div className="p-6">Not found.</div>,
});

function MapPage() {
  const { t } = useLanguage();
  const [view, setView] = useState<"places" | "destinations">("destinations");

  return (
    <div className="relative h-[calc(100dvh-4rem)] overflow-hidden bg-[#eef1f6]">
      {view === "destinations" ? (
        <>
          <FinlandCitiesMap />
          {/* Floating switch — destinations view has no toolbar */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex h-9 items-center rounded-full bg-white/95 shadow-lg border border-navy/10 p-0.5">
            {(["destinations", "places"] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`h-8 px-3.5 rounded-full text-[11px] font-semibold leading-none whitespace-nowrap transition-colors ${
                  view === v ? "bg-navy text-white shadow-sm" : "text-navy/55 hover:text-navy"
                }`}
              >
                {t(v === "destinations" ? "map.destinations" : "map.places")}
              </button>
            ))}
          </div>
        </>
      ) : (
        <PlatformMap view={view} onViewChange={setView} />
      )}
    </div>
  );
}
